/**
 * Heat conduction, combustion, and phase change.
 *
 * The design commitment here: **fire is not a system.** There is no fire
 * spread algorithm. There is heat conduction, an ignition threshold, and a
 * fuel timer. Oil catches faster than wood because its `ignitionTemp` is lower
 * and its `burnTemp` is higher, not because of an `if (oil)` branch. Every
 * flammable material a future workstream adds burns correctly for free.
 *
 * Phase changes are the same idea: melting, freezing, boiling and condensing
 * are four threshold comparisons against material-declared targets.
 */

import { profiler } from '../core/profiler';
import type { Rng } from '../core/rng';
import { hash2 } from '../core/rng';
import { Flag, M, VOID } from './materials';
import { CHUNK_MASK, CHUNK_SHIFT, CHUNK_SIZE, CellWorld, clampTemp, type Chunk } from './world';

/** Global conduction rate. Above ~0.2 the explicit scheme goes unstable. */
const CONDUCTION = 0.14;

/** Ambient temperature that unbounded space relaxes toward. */
const AMBIENT = 20;

/** How strongly a burning cell heats its neighbours, per tick. */
const COMBUSTION_RADIANCE = 0.35;

export class Thermal {
  private world: CellWorld;
  private rng: Rng;

  /** Scratch buffer for the conduction pass — one chunk's worth, reused. */
  private scratch = new Int16Array(1 << (CHUNK_SHIFT * 2));

  ignitionsThisTick = 0;
  transitionsThisTick = 0;

  /**
   * Cells visited by the conduction/reaction pass this tick.
   *
   * Counted inside `pass`, from the band `pass` was actually handed — not from
   * the row arithmetic in `step` that decided how much quota to spend. The two
   * agree today and the distinction is the whole point: the pre-fix
   * `wide = c.burning > 0` widening scanned all 16384 cells of a burning chunk
   * while `step`'s arithmetic still priced the narrow dirty rect, so a counter
   * kept in `step` reported a quota that was being honoured and a system running
   * at 4.3 ms inside a 1.0 ms line item. A counter that lives where the loop
   * lives cannot be lied to that way, which is what makes it a fixture the
   * budget tests can assert on instead of the wall clock (docs/01 §9).
   */
  cellsThisTick = 0;

  /** Fired when a cell ignites, so presentation can spark without polling. */
  onIgnite: ((x: number, y: number, mat: number) => void) | null = null;

  /**
   * Fired when a cell stops burning — burnout, quench, or fuel exhaustion.
   *
   * The symmetric half of `onIgnite`. Ignition used to be the only material
   * state change the world could tell the player about, and "the fire went out"
   * is at least as informative as "it caught" — it is the readable half of the
   * wet-resists-ignition rule in docs/06 §4.
   */
  onExtinguish: ((x: number, y: number, mat: number) => void) | null = null;

  constructor(world: CellWorld, rng: Rng) {
    this.world = world;
    this.rng = rng;
  }

  /**
   * Deterministic work quota per tick, **in cells** — not in chunks.
   *
   * A chunk quota is not a quota. One unit spanned a ~2000× cost range: a chunk
   * with a 4-cell dirty rect and a chunk with one burning cell (which used to
   * widen the pass to all 16384) counted the same against `maxChunksPerTick`,
   * so the worst case was 12 × 16384 × 2 passes = 393,216 cell-visits inside a
   * 1.0 ms line item. Cells are the thing that costs time, so cells are the
   * thing the quota counts — the same denomination `cellsim.ts` uses, for the
   * same reason.
   *
   * Calibrated against the 1.0 ms budget in docs/01 §9: a visited cell measures
   * 80–106 ns across the stress scenes (Node 22, darwin/arm64), so 9000 cells
   * is 0.72–0.95 ms. This is the one quota that genuinely binds — a screen of
   * burning oil wants far more than 9000 cells a tick, and capping it is the
   * point.
   */
  maxCellsPerTick = 9000;

  /** Resume point, by chunk identity — the list is rebuilt every tick. */
  private cursorCx = 0;
  private cursorCy = 0;
  private cursorValid = false;
  private cursorRow = -1;

  step(): void {
    profiler.begin('thermal');
    this.ignitionsThisTick = 0;
    this.transitionsThisTick = 0;
    this.cellsThisTick = 0;

    const active = this.world.activeChunks;
    if (active.length === 0) {
      this.cursorValid = false;
      this.cursorRow = -1;
      profiler.end('thermal');
      return;
    }

    // Resume where the quota parked us, by identity. Index-based resumption
    // would land on a different chunk whenever the active set changed.
    let start = 0;
    let resumeRow = -1;
    if (this.cursorValid) {
      let found = -1;
      for (let i = 0; i < active.length; i++) {
        const c = active[i];
        if (c.cx === this.cursorCx && c.cy === this.cursorCy) {
          found = i;
          break;
        }
      }
      if (found >= 0) {
        start = found;
        resumeRow = this.cursorRow;
      }
      this.cursorValid = false;
      this.cursorRow = -1;
    }

    let budget = this.maxCellsPerTick;

    // One sweep from the resume point to the end of the list, then wrap to the
    // head. Wrapping matters: without it the chunks before the cursor would
    // starve for a whole lap whenever the quota bit early.
    for (let n = 0; n < active.length; n++) {
      const ci = start + n < active.length ? start + n : start + n - active.length;
      const c = active[ci];

      // The rect is the union of "matter moved" and "temperature changed" — see
      // world.ts. `burning > 0` no longer widens anything: a burning cell
      // re-touches itself every tick, so it is already in the rect along with
      // the one-cell dilation ring conduction needs in order to advance.
      //
      // Union the two only when both are live. An empty rect carries stale
      // bounds on the axis it does not reset, and folding those in silently
      // inflates the rect — which is quota spent on cells that cannot change.
      const hasMove = c.dirtyMaxX >= c.dirtyMinX;
      const hasTherm = c.thermMaxX >= c.thermMinX;
      if (!hasMove && !hasTherm) continue;
      let minX: number;
      let maxX: number;
      let minY: number;
      let maxY: number;
      if (!hasTherm) {
        minX = c.dirtyMinX;
        maxX = c.dirtyMaxX;
        minY = c.dirtyMinY;
        maxY = c.dirtyMaxY;
      } else if (!hasMove) {
        minX = c.thermMinX;
        maxX = c.thermMaxX;
        minY = c.thermMinY;
        maxY = c.thermMaxY;
      } else {
        minX = c.dirtyMinX < c.thermMinX ? c.dirtyMinX : c.thermMinX;
        maxX = c.dirtyMaxX > c.thermMaxX ? c.dirtyMaxX : c.thermMaxX;
        minY = c.dirtyMinY < c.thermMinY ? c.dirtyMinY : c.thermMinY;
        maxY = c.dirtyMaxY > c.thermMaxY ? c.dirtyMaxY : c.thermMaxY;
      }
      if (maxY < minY) continue;

      const width = maxX - minX + 1;
      let y0 = minY;
      if (n === 0 && resumeRow > minY) y0 = resumeRow > maxY ? maxY : resumeRow;

      // How many rows fit in what is left of the quota. Always at least one, so
      // a chunk wider than the whole quota still makes progress instead of
      // deadlocking.
      let rows = (budget / width) | 0;
      if (rows < 1) rows = 1;
      let y1 = y0 + rows - 1;
      const parked = y1 < maxY;
      if (!parked) y1 = maxY;

      this.pass(c, minX, maxX, y0, y1);
      const spent = (y1 - y0 + 1) * width;
      budget -= spent;

      if (parked) {
        this.cursorCx = c.cx;
        this.cursorCy = c.cy;
        this.cursorRow = y1 + 1;
        this.cursorValid = true;
        break;
      }
      if (budget <= 0) {
        // Exhausted exactly at a chunk boundary: park on the next chunk so the
        // sweep continues rather than restarting at the head of the list.
        const nx = ci + 1 < active.length ? ci + 1 : 0;
        this.cursorCx = active[nx].cx;
        this.cursorCy = active[nx].cy;
        this.cursorRow = -1;
        this.cursorValid = true;
        break;
      }
    }

    profiler.setCount('thermalCells', this.cellsThisTick);
    // Telemetry only, and O(resident chunks): worth a full scan when someone is
    // looking at the overlay, not worth one on every tick of every session.
    if (profiler.enabled) profiler.setCount('burning', this.world.countBurning());
    profiler.end('thermal');
  }

  /**
   * Conduction and reaction over one row band of one chunk.
   *
   * Conduction reads into `scratch` for the whole band first, then writes back,
   * so the pass is order-independent within the band — an in-place update would
   * let heat propagate several cells in one tick in the scan direction, which
   * shows up as fire visibly racing left.
   *
   * The write-back is fused into the reaction loop. It used to be a third full
   * traversal of the rect; reaction reads only its own cell's temperature, and
   * that cell is written immediately before the reaction logic sees it, so the
   * fused order produces identical results for a third less pointer chasing.
   */
  private pass(c: Chunk, minX: number, maxX: number, minY: number, maxY: number): void {
    this.cellsThisTick += (maxX - minX + 1) * (maxY - minY + 1);
    const baseX = c.cx << CHUNK_SHIFT;
    const baseY = c.cy << CHUNK_SHIFT;
    const temp = c.temp;
    const mat = c.mat;
    const flags = c.flags;
    const scratch = this.scratch;
    const conductivity = M.conductivity;

    for (let y = minY; y <= maxY; y++) {
      const row = y << CHUNK_SHIFT;
      const wy = baseY + y;
      // Rows and columns on the chunk edge have to ask the world for their
      // neighbours; everything else reads the local arrays directly. Splitting
      // the two cases keeps four bounds checks and a chunk lookup out of the
      // inner loop for 98% of cells.
      const edgeRow = y === 0 || y === CHUNK_MASK;

      for (let x = minX; x <= maxX; x++) {
        const i = row | x;
        const m = mat[i];
        const t = temp[i];
        const cond = conductivity[m];

        let flux: number;
        if (!edgeRow && x > 0 && x < CHUNK_MASK) {
          const up = i - CHUNK_SIZE;
          const dn = i + CHUNK_SIZE;
          flux =
            (temp[i - 1] - t) * cond * conductivity[mat[i - 1]] +
            (temp[i + 1] - t) * cond * conductivity[mat[i + 1]] +
            (temp[up] - t) * cond * conductivity[mat[up]] +
            (temp[dn] - t) * cond * conductivity[mat[dn]];
        } else {
          flux =
            this.neighbourFlux(c, x - 1, y, baseX - 1 + x, wy, t, cond) +
            this.neighbourFlux(c, x + 1, y, baseX + 1 + x, wy, t, cond) +
            this.neighbourFlux(c, x, y - 1, baseX + x, wy - 1, t, cond) +
            this.neighbourFlux(c, x, y + 1, baseX + x, wy + 1, t, cond);
        }

        // Burning cells radiate. This is the entire fire-spread mechanism.
        if (flags[i] & Flag.BURNING) {
          flux += (M.burnTemp[m] - t) * COMBUSTION_RADIANCE;
        }

        // Empty space bleeds toward ambient so rooms don't stay superheated
        // forever after a fire burns out.
        if (m === VOID) flux += (AMBIENT - t) * 0.05;

        scratch[i] = clampTemp(t + (flux * CONDUCTION) / M.heatCapacity[m]);
      }
    }

    this.react(c, minX, maxX, minY, maxY);
  }

  private neighbourFlux(
    c: Chunk,
    nlx: number,
    nly: number,
    wx: number,
    wy: number,
    t: number,
    cond: number,
  ): number {
    let nt: number;
    let nm: number;
    if (nlx >= 0 && nlx <= CHUNK_MASK && nly >= 0 && nly <= CHUNK_MASK) {
      const ni = (nly << CHUNK_SHIFT) | nlx;
      nt = c.temp[ni];
      nm = c.mat[ni];
    } else {
      const nc = this.world.peekChunkAt(wx, wy);
      if (nc === null) return (AMBIENT - t) * 0.02;
      const ni = ((wy & CHUNK_MASK) << CHUNK_SHIFT) | (wx & CHUNK_MASK);
      nt = nc.temp[ni];
      nm = nc.mat[ni];
    }
    // Pair conductivity is the geometric-ish mean: an insulator on either side
    // of a boundary throttles the whole exchange, which is what makes ceramic
    // and voidglass actually work as heat shields.
    const pair = cond * M.conductivity[nm];
    return (nt - t) * pair;
  }

  /**
   * Conduction write-back plus threshold-driven state change: ignition,
   * burnout, and the four phase transitions. Runs over the band conduction
   * just computed.
   */
  private react(c: Chunk, minX: number, maxX: number, minY: number, maxY: number): void {
    const baseX = c.cx << CHUNK_SHIFT;
    const baseY = c.cy << CHUNK_SHIFT;
    const temp = c.temp;
    const scratch = this.scratch;
    const w = this.world;

    for (let y = minY; y <= maxY; y++) {
      const row = y << CHUNK_SHIFT;
      for (let x = minX; x <= maxX; x++) {
        const i = row | x;

        // Write-back. `touchTemp` is the fix for a silent correctness bug: the
        // old write-back wrote `temp[i] = scratch[i]` and touched nothing, so
        // pure conduction set no render-dirty flag and the temperature texture
        // went stale — a wall heating toward ignition did not visibly glow
        // until something else happened to touch those cells. It also left the
        // conducting region out of next tick's rect, which is the reason the
        // whole-chunk widening existed at all.
        if (temp[i] !== scratch[i]) {
          temp[i] = scratch[i];
          c.touchTemp(x, y);
          // On a chunk edge the dilation ring falls outside this chunk, so the
          // neighbour has to be told or conduction stops at the seam.
          if (x === 0 || x === CHUNK_MASK || y === 0 || y === CHUNK_MASK) {
            w.wakeNeighboursTemp(c, x, y);
          }
        }

        const m = c.mat[i];
        if (m === VOID) continue;
        const t = temp[i];
        const wx = baseX + x;
        const wy = baseY + y;
        const flags = c.flags[i];

        // --- combustion ---
        if (flags & Flag.BURNING) {
          const fuel = c.aux[i];
          if (fuel <= 1) {
            const ash = M.ashesInto[m];
            c.burning--;
            c.flags[i] &= ~Flag.BURNING;
            if (this.onExtinguish) this.onExtinguish(wx, wy, m);
            if (ash >= 0) w.transmute(wx, wy, ash);
            else w.set(wx, wy, VOID);
            c.touch(x, y);
            continue;
          }
          c.aux[i] = fuel - 1;
          // Temperature-only: a burning cell is not, by virtue of burning,
          // matter that needs a movement pass. It still re-enters the thermal
          // rect (which is what keeps fire spreading) and still re-uploads
          // (which is what keeps it flickering).
          c.touchTemp(x, y);
        } else {
          const ign = M.ignitionTemp[m];
          if (ign !== 32767 && t >= ign) {
            // Wet matter resists ignition — the single flag that makes
            // "spray water on yourself before running through fire" work.
            if (!(flags & Flag.WET)) {
              c.flags[i] |= Flag.BURNING;
              c.burning++;
              c.aux[i] = M.burnDuration[m];
              c.touch(x, y);
              this.ignitionsThisTick++;
              if (this.onIgnite) this.onIgnite(wx, wy, m);
            } else if ((hash2(wx, wy, w.tick) & 63) === 0) {
              // Heat eventually boils the film off.
              c.flags[i] &= ~Flag.WET;
            }
          }
        }

        // --- phase transitions ---
        // Probabilistic rather than instant: a pool at exactly 0 °C shouldn't
        // flash-freeze in one tick, it should crust over from the edges.
        const melt = M.meltsAt[m];
        if (melt !== 32767 && t >= melt && M.meltsInto[m] >= 0) {
          if ((hash2(wx, wy, w.tick * 5) & 7) === 0) {
            w.transmute(wx, wy, M.meltsInto[m]);
            this.transitionsThisTick++;
            continue;
          }
        }
        const boil = M.boilsAt[m];
        if (boil !== 32767 && t >= boil && M.boilsInto[m] >= 0) {
          if ((hash2(wx, wy, w.tick * 11) & 7) === 0) {
            w.transmute(wx, wy, M.boilsInto[m]);
            this.transitionsThisTick++;
            continue;
          }
        }
        const freeze = M.freezesAt[m];
        if (freeze !== -32768 && t <= freeze && M.freezesInto[m] >= 0) {
          if ((hash2(wx, wy, w.tick * 13) & 7) === 0) {
            w.transmute(wx, wy, M.freezesInto[m]);
            this.transitionsThisTick++;
            continue;
          }
        }
        const cond = M.condensesAt[m];
        if (cond !== -32768 && t <= cond && M.condensesInto[m] >= 0) {
          if ((hash2(wx, wy, w.tick * 17) & 7) === 0) {
            w.transmute(wx, wy, M.condensesInto[m]);
            this.transitionsThisTick++;
            continue;
          }
        }

        // --- brittleness from cold ---
        // Frozen matter is the setup half of the frost+impact combo; the
        // shatter itself lives in weapon/ability code via damageCell.
        if (t < -5 && M.brittleness[m] > 0.4 && !(c.flags[i] & Flag.FROZEN)) {
          c.flags[i] |= Flag.FROZEN;
          c.touch(x, y);
        } else if (t > 5 && c.flags[i] & Flag.FROZEN) {
          c.flags[i] &= ~Flag.FROZEN;
          // Mobility just changed, so this is a movement touch, not a thermal
          // one: thawed matter has to be handed back to the cell sim or it
          // hangs in the air until something else disturbs it.
          c.touch(x, y);
        }
      }
    }
  }

  /** Inject heat at a point — the entry point for weapons and abilities. */
  ignite(x: number, y: number, radius = 1): void {
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx * dx + dy * dy > radius * radius) continue;
        const m = this.world.peek(x + dx, y + dy);
        if (m === VOID) continue;
        const ign = M.ignitionTemp[m];
        if (ign === 32767) continue;
        this.world.setTemp(x + dx, y + dy, ign + 40);
      }
    }
  }
}
